const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: any[]) => void;
  }
}

export type AnalyticsParams = Record<
  string,
  string | number | boolean | undefined
>;

const hasGtag = (): boolean => typeof window.gtag === "function";

/** Fire a GA4 event when gtag is available. */
export const trackEvent = (
  eventName: string,
  params: AnalyticsParams = {}
): void => {
  if (!MEASUREMENT_ID || !hasGtag()) return;
  window.gtag("event", eventName, params);
};

/** SPA page view — call on every React Router location change. */
export const trackPageView = (path: string, title?: string): void => {
  if (!MEASUREMENT_ID || !hasGtag()) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
};

export const trackBookClick = (place: string): void => {
  trackEvent("click_book", { place });
};

export const trackPricesClick = (place: string, category?: string): void => {
  trackEvent("click_prices", {
    place,
    ...(category != null ? { category } : {}),
  });
};

export const trackSocialClick = (network: string): void => {
  trackEvent("click_social", { network });
};

export const trackBookingStep = (step: number): void => {
  trackEvent("booking_step", { step });
};

export const trackBookingSubmit = (): void => {
  trackEvent("booking_submit");
};

const loadGtag = (): void => {
  if (!MEASUREMENT_ID) {
    return;
  }

  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js requires the Arguments object itself, not an array
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  // SPA: we send page_view ourselves on route changes
  window.gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  script.onload = () => {
    trackPageView(
      `${window.location.pathname}${window.location.search}`,
      document.title
    );
  };
  document.head.appendChild(script);
};

/** Defer GA until the browser is idle so it stays off the critical path. */
export const initAnalytics = (): void => {
  if (!MEASUREMENT_ID) {
    return;
  }

  const schedule =
    typeof window.requestIdleCallback === "function"
      ? (cb: () => void) => {
          window.requestIdleCallback(cb, { timeout: 4000 });
        }
      : (cb: () => void) => {
          window.setTimeout(cb, 2000);
        };

  if (document.readyState === "complete") {
    schedule(loadGtag);
    return;
  }

  window.addEventListener(
    "load",
    () => {
      schedule(loadGtag);
    },
    { once: true }
  );
};
